import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Universal Components
import UniversalHeader from "./components/UniversalHeader/UniversalHeader";
import UniversalSidebar from "./components/UniversalSidebar/UniversalSidebar";
import MobileFooter from "./Views/MobileFooter";
import DesktopFooter from "./Views/DesktopFooter";

// Analytics
import usePageTracking from "./hooks/usePageTracking";

// Auth
import AuthProvider from "./auth/AuthProvider";
import { UsernameProvider } from "./hooks/useUsername";

// One Piece Quizzes
import QuizHome from "./Views/QuizHome";
import AnotherOnePiece from "./Views/AnotherOnePiece";
import OnePieceImageQuiz from "./Views/OnePieceImageQuiz";
import DevilFruitQuiz from "./Views/DevilFruitQuiz";
import BountyBattle from "./Views/BountyBattle";
import WhichCharacter from "./Views/WhichCharacter";
import WantedPoster from "./Views/WantedPoster";
import CrewBuilder from "./Views/CrewBuilder";
import Room from "./Views/Room";
import ColorGuess from "./Views/ColorGuess";
import JigsawPuzzle from "./Views/JigsawPuzzle";
import MyGames from "./Views/MyGames";
import NewGameToast from "./components/NewGameToast/NewGameToast";
import { GuestNudgeToast } from "./components/GuestNudge/GuestNudge";
import PageNav from "./components/PageNav/PageNav";
import ScrollMemory from "./components/ScrollMemory/ScrollMemory";
import BountyRankings from "./Views/BountyRankings";
import DevilFruitDirectory from "./Views/DevilFruitDirectory";
import CharacterCompare from "./Views/CharacterCompare";
import QuizStats from "./Views/QuizStats";
import Leaderboard from "./Views/Leaderboard";
import Profile from "./Views/Profile";
import Login from "./Views/Login";

// Legal Pages
import PrivacyPolicy from "./Views/PrivacyPolicy";
import TermsOfService from "./Views/TermsOfService";
import About from "./Views/About";
import Contact from "./Views/Contact";

// Content Guides
import GuidesIndex from "./Views/GuidesIndex";
import GuideStrawHatCrew from "./Views/GuideStrawHatCrew";
import GuideDevilFruits from "./Views/GuideDevilFruits";
import GuideCharacters from "./Views/GuideCharacters";
import CharactersIndex from "./Views/CharactersIndex";
import CharacterProfile from "./Views/CharacterProfile";
import ArcsPage from "./Views/ArcsPage";
import ArcDetail from './Views/ArcDetail';
import NotFound from './Views/NotFound';

import "font-awesome/css/font-awesome.min.css";
import "./App.css";

// Page tracking component (must be inside Router)
const PageTracker = () => {
  usePageTracking();
  return null;
};

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <UsernameProvider>
        <Router>
          {/* Analytics Page Tracking */}
          <PageTracker />

          <div className="app-container">
            {/* Universal Header */}
            <UniversalHeader
              onToggleSidebar={toggleSidebar}
              isSidebarOpen={isSidebarOpen}
            />

            {/* Universal Sidebar */}
            <UniversalSidebar
              isOpen={isSidebarOpen}
              onClose={closeSidebar}
            />

            {/* Main Content */}
            <main className="main-content">
              <ScrollMemory />
              <PageNav />
              <Routes>
                {/* Quiz Routes */}
                <Route path="/" element={<QuizHome />} />
                <Route path="/quiz" element={<AnotherOnePiece animeIds={[21]} theme="onePiece" />} />
                <Route path="/image-quiz" element={<OnePieceImageQuiz animeIds={[21]} theme="onePiece" />} />
                <Route path="/devil-fruit-quiz" element={<DevilFruitQuiz animeIds={[21]} />} />
                <Route path="/bounty-battle" element={<BountyBattle />} />
                <Route path="/which-character" element={<WhichCharacter />} />
                <Route path="/wanted" element={<WantedPoster />} />
                <Route path="/crew" element={<CrewBuilder />} />
                <Route path="/room/:code" element={<Room />} />
                {/* A test, not linked anywhere and noindex. */}
                <Route path="/color-test" element={<ColorGuess />} />
                <Route path="/puzzle" element={<JigsawPuzzle />} />
                <Route path="/history" element={<MyGames />} />

                {/* Reference tables built from characterProfiles. These target the
                    query family Search Console already shows us ranking for
                    (bounties, devil fruits) with data rather than prose. */}
                <Route path="/bounties" element={<BountyRankings />} />
                <Route path="/fruits" element={<DevilFruitDirectory />} />
                <Route path="/compare" element={<CharacterCompare />} />

                {/* First-party data: what real players actually score. */}
                <Route path="/quiz-stats" element={<QuizStats />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/login" element={<Login />} />

                {/* Legal Pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />

                {/* Content Guides */}
                <Route path="/guides" element={<GuidesIndex />} />
                <Route path="/guides/straw-hat-crew" element={<GuideStrawHatCrew />} />
                <Route path="/guides/devil-fruits" element={<GuideDevilFruits />} />
                <Route path="/guides/characters" element={<GuideCharacters />} />

                {/* Character Profiles */}
                <Route path="/characters" element={<CharactersIndex />} />
                <Route path="/characters/:slug" element={<CharacterProfile />} />
                <Route path="/arcs" element={<ArcsPage />} />
                <Route path="/arcs/:slug" element={<ArcDetail />} />

                {/* Fallback: a real "not found", not a silent copy of the homepage */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>

            {/* Footer */}
            {/* Both render; the stylesheets already pick one by width and are
                complementary (mobile hides above 768px, desktop hides below).
                Choosing in JS off window.innerWidth picked once at mount with no
                resize listener, so loading narrow and then widening left the
                hidden footer in the DOM and no footer — and no disclaimer — on
                screen at all. */}
            <NewGameToast />
            <GuestNudgeToast />
            <MobileFooter />
            <DesktopFooter />
          </div>
        </Router>
        </UsernameProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
